import { Box, Card, CardActionArea, CardContent, Stack, Typography } from '@mui/material';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import AuthenticatedImage from '../../core/AuthenticatedImage';
import { BlogPostPreview } from '../blogSchemas';
import { formatDateTime } from '../blogUtils';
import TagChip from '../../core/TagChip';

const WAPPEN_SRC = `${process.env.PUBLIC_URL}/Wappen_PVTA.png`;

interface BlogPostCardProps {
    post: BlogPostPreview;
    onClick: (id: number) => void;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post, onClick }) => {
    const mainImageUrl = post.coverImageUrl ?? post.previewImages[0]?.url ?? null;
    const galleryCount = post.previewImages.length;

    return (
        <Card
            sx={{
                borderRadius: 'var(--pvta-radius-md)',
                border: '1px solid var(--pvta-border)',
                boxShadow: 'var(--pvta-shadow-card)',
                overflow: 'hidden',
                height: '100%',
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 'var(--pvta-shadow-hover)' },
            }}
        >
            <CardActionArea
                onClick={() => onClick(post.id)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
            >
                <Box sx={{ position: 'relative', height: { xs: 180, md: 200 }, width: '100%' }}>
                    {mainImageUrl ? (
                        <AuthenticatedImage
                            url={mainImageUrl}
                            alt={post.title}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundImage: 'linear-gradient(135deg, #1f4d2b 0%, #0f2f1a 100%)',
                            }}
                        >
                            <Box component="img" src={WAPPEN_SRC} alt="" sx={{ height: 64, opacity: 0.9 }} />
                        </Box>
                    )}
                    {post.tags.length > 0 && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ position: 'absolute', top: 12, left: 12, maxWidth: 'calc(100% - 24px)' }}
                        >
                            {post.tags.slice(0, 3).map((tag) => (
                                <TagChip key={tag.id} tag={tag} />
                            ))}
                        </Stack>
                    )}
                    {galleryCount > 1 && (
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{
                                position: 'absolute',
                                bottom: 12,
                                right: 12,
                                px: 1,
                                py: '3px',
                                borderRadius: 999,
                                backgroundColor: 'rgba(15, 47, 26, 0.78)',
                                color: '#ffffff',
                            }}
                        >
                            <PhotoLibraryOutlinedIcon sx={{ fontSize: 14 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {`+${galleryCount - 1} Fotos`}
                            </Typography>
                        </Stack>
                    )}
                </Box>
                <CardContent sx={{ minWidth: 0, width: '100%', flex: 1 }}>
                    <Stack spacing={1}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: 20,
                                color: 'var(--pvta-text)',
                                lineHeight: 1.25,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {post.title}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: '#4f5b52',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {post.teaser || post.fallbackText}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--pvta-muted)' }}>
                            {post.author.name} · {formatDateTime(post.createdAt)}
                        </Typography>
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default BlogPostCard;
